import moment from 'moment';
import { type RefObject, useEffect, useRef } from 'react';

const DAY_SELECTOR = 'button[data-date]';
const MAX_STEPS = 62;

const STEPS: Record<string, number> = {
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -7,
  ArrowDown: 7,
};

const findDay = (root: HTMLElement, date: moment.Moment) =>
  root.querySelector<HTMLButtonElement>(
    `${DAY_SELECTOR}[data-date="${date.format('YYYY-MM-DD')}"]`
  );

const findEnabledDay = (
  root: HTMLElement,
  from: moment.Moment,
  step: number,
  limit: number,
  skipMissing = false
) => {
  const date = from.clone();
  for (let i = 0; i < limit; i++) {
    date.add(step, 'day');
    const day = findDay(root, date);
    if (!day) {
      if (skipMissing) continue;
      return null;
    }
    if (!day.disabled) return day;
  }
  return null;
};

export function useCalendarKeyboard(rootRef: RefObject<HTMLElement | null>) {
  const lastFocusedRef = useRef<string | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const days = Array.from(root.querySelectorAll<HTMLButtonElement>(DAY_SELECTOR));
    const enabled = days.filter((day) => !day.disabled);
    const tabStop =
      enabled.find((day) => day.dataset.date === lastFocusedRef.current) ??
      enabled.find((day) => day.hasAttribute('data-selected')) ??
      enabled[0];
    days.forEach((day) => {
      day.tabIndex = day === tabStop ? 0 : -1;
    });
  });

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const handleFocusIn = (event: FocusEvent) => {
      const day = (event.target as Element | null)?.closest?.<HTMLButtonElement>(DAY_SELECTOR);
      if (!day) return;
      lastFocusedRef.current = day.dataset.date ?? null;
      root.querySelectorAll<HTMLButtonElement>(DAY_SELECTOR).forEach((other) => {
        other.tabIndex = other === day ? 0 : -1;
      });
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const day = (event.target as Element | null)?.closest?.<HTMLButtonElement>(DAY_SELECTOR);
      const date = day?.dataset.date;
      if (!date) return;
      const current = moment(date, 'YYYY-MM-DD');

      let next: HTMLButtonElement | null = null;
      if (event.key in STEPS) {
        next = findEnabledDay(root, current, STEPS[event.key], MAX_STEPS);
      } else if (event.key === 'Home') {
        const weekStart = current.clone().subtract(current.isoWeekday(), 'day');
        next = findEnabledDay(root, weekStart, 1, current.isoWeekday() - 1, true);
      } else if (event.key === 'End') {
        const weekEnd = current.clone().add(8 - current.isoWeekday(), 'day');
        next = findEnabledDay(root, weekEnd, -1, 7 - current.isoWeekday(), true);
      }

      if (!next) return;
      event.preventDefault();
      next.focus();
    };

    root.addEventListener('focusin', handleFocusIn);
    root.addEventListener('keydown', handleKeyDown);
    return () => {
      root.removeEventListener('focusin', handleFocusIn);
      root.removeEventListener('keydown', handleKeyDown);
    };
  }, [rootRef]);
}
